import { PlusOutlined } from '@ant-design/icons';
import { MultiSelect } from '@mantine/core';
import { Button, Card, Col, Form, Row, Select, Table } from 'antd';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import { NumberContext } from 'contexts/legacy/NumberContext';
import { Number, NumberInfo } from 'interfaces/number';
import React, { useContext, useEffect, useState } from 'react';
import SegmentModal from './SegmentModal';
import TextArea from 'antd/lib/input/TextArea';
import { Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { apiV2 } from 'services/api';
import CRMField from './types/CrmFields.type';
import { LanguageContext } from 'contexts/LanguageContext';
interface CampaignAudienceProps {
	originNumber: string;
	originNumberMessage?: string;
	onChangeOriginNumber: (value: string) => void;
	audience: string;
	audienceFieldName: string;
	audienceMessage?: string;
	onChangeAudience: (value: string) => void;
	segment: string[];
	segmentMessage?: string[];
	onChangeSegment: (value: string[]) => void;
}

function CampaignAudience(props: Readonly<CampaignAudienceProps>) {
	const [domain, setDomain] = useState<string>();
	const [activeNumbers, setActiveNumbers] = useState<NumberInfo[]>([]);
	const [audienceType, setAudienceType] = useState<number>();
	const [segmentModalIsVisible, setSegmentModalIsVisible] = useState(false);
	const [segments, setSegments] = useState<any[]>([]);
	const [selectSegmentsItems, setSelectSegmentsItems] = useState<any[]>([]);
	const [chosenSegments, setChosenSegments] = useState<any[]>([]);
	const [segmentStatus, setSegmentStatus] = useState<string>();
	const [dealFields, setDealFields] = useState<CRMField[]>([]);
	const [leadFields, setLeadFields] = useState<CRMField[]>([]);
	const [contactFields, setContactFields] = useState<CRMField[]>([]);
	const [companyFields, setCompanyFields] = useState<CRMField[]>([]);
	const numberContext = useContext<Number>(NumberContext);
	const originStatus = props.originNumberMessage ? 'error' : undefined;
	const audienceStatus = props.audienceMessage ? 'error' : undefined;
	const { dictionary } = useContext(LanguageContext);

	useEffect(() => {
		if (props.segment && props.segment.length > 0) {
			setChosenSegments(props.segment);
			setAudienceType(1);
		}

		if (props.audience !== '') {
			props.onChangeAudience(props.audience);
			setAudienceType(0);
		}
	}, []);

	useEffect(() => {
		setSegmentStatus(props.segmentMessage ? '#f5222d' : undefined);
	}, [props.segmentMessage]);

	useEffect(() => {
		if (!domain) return;
		fetchSegments();
		fetchEntityFields();
	}, [domain]);

	useEffect(() => {
		setActiveNumbers(numberContext.numbers.actives.map((number) => number));
	}, [numberContext.numbers.actives]);

	const bitrix24 = useContext(Bitrix24Context);
	useEffect(() => {
		if (!bitrix24.infos.domain) return;
		setDomain(bitrix24.infos.domain);
		apiV2.interceptors.request.use((config) => {
			return {
				...config,
				headers: {
					...config.headers,
					'Access-Token': bitrix24.auth.access_token,
					'Refresh-Token': bitrix24.auth.refresh_token,
				},
			};
		});
	}, [bitrix24.auth, bitrix24.infos.domain]);

	function handleSelectSegments(segments: string[]) {
		setChosenSegments(segments);
		props.onChangeSegment(segments);
	}

	function fetchSegments() {
		setSegmentModalIsVisible(false);
		apiV2.get(`/segment?domain=${domain}`).then((response) => {
			const segments: any[] = response.data;
			segments.push(
				{
					id: 'contacts',
					name: dictionary.all_contacts,
				},
				{
					id: 'companies',
					name: dictionary.all_companies,
				},
				{
					id: 'leads',
					name: dictionary.all_leads,
				},
				{
					id: 'deals',
					name: dictionary.all_deals,
				},
				{
					id: 'all',
					name: dictionary.all_base,
				},
			);
			setSegments(segments);
			setSelectSegmentsItems(
				segments.reverse().map((segment) => ({
					value: segment.id,
					label: segment.name,
				})),
			);
		});
	}

	function fetchEntityFields() {
		const entities = ['deals', 'leads', 'contacts', 'companies'];

		entities.forEach((entity) => {
			if (entity === 'deals' && dealFields.length > 0) return;
			if (entity === 'leads' && leadFields.length > 0) return;
			if (entity === 'contacts' && contactFields.length > 0) return;
			if (entity === 'companies' && companyFields.length > 0) return;

			apiV2.get(`${entity}/${domain}/fields`).then((response) => {
				const fields = response.data;
				if (entity === 'deals') setDealFields(fields);
				if (entity === 'leads') setLeadFields(fields);
				if (entity === 'contacts') setContactFields(fields);
				if (entity === 'companies') setCompanyFields(fields);
			});
		});
	}

	function toggleSegmentModal() {
		setSegmentModalIsVisible(!segmentModalIsVisible);
	}

	function replaceFilterKeysForTitles(filters: Record<string, string>, fields: CRMField[]) {
		const newFilters: [string, string][] = [];
		Object.entries(filters).forEach(([key, value]) => {
			const formattedKey = key.replace(/[^a-zA-Z0-9_]/g, '');
			const field = fields.find((field) => field.key === formattedKey);
			const title = field ? field.title : key;
			const itemValue = field && field.items?.find((item) => item.ID === value)?.VALUE;
			newFilters.push([title, itemValue || value]);
		});

		return newFilters;
	}

	function chosenSegmentsHasFilters() {
		const cSegments = segments.filter((segment) => chosenSegments.includes(segment.id));
		const hasFilters = cSegments.some((segment) => {
			const dealsFilter = segment.dealsFilter || {};
			const contactsFilter = segment.contactsFilter || {};
			const companiesFilter = segment.companiesFilter || {};
			const leadsFilter = segment.leadsFilter || {};

			return (
				Object.keys(dealsFilter).length > 0 ||
				Object.keys(contactsFilter).length > 0 ||
				Object.keys(companiesFilter).length > 0 ||
				Object.keys(leadsFilter).length > 0
			);
		});

		return hasFilters;
	}

	function getFormattedChosenSegments() {
		const cSegments = segments.filter((segment) => chosenSegments.includes(segment.id));
		const dealsFilter = cSegments
			.map((segment) => segment.dealsFilter)
			.reduce((acc, filter) => ({ ...acc, ...filter }), {});
		const contactsFilter = cSegments
			.map((segment) => segment.contactsFilter)
			.reduce((acc, filter) => ({ ...acc, ...filter }), {});
		const companiesFilter = cSegments
			.map((segment) => segment.companiesFilter)
			.reduce((acc, filter) => ({ ...acc, ...filter }), {});
		const leadsFilter = cSegments
			.map((segment) => segment.leadsFilter)
			.reduce((acc, filter) => ({ ...acc, ...filter }), {});

		const dealsFilters = replaceFilterKeysForTitles(dealsFilter, dealFields);
		const leadsFilters = replaceFilterKeysForTitles(leadsFilter, leadFields);
		const contactsFilters = replaceFilterKeysForTitles(contactsFilter, contactFields);
		const companiesFilters = replaceFilterKeysForTitles(companiesFilter, companyFields);

		return Object.entries({ dealsFilters, contactsFilters, companiesFilters, leadsFilters });
	}

	function getColums(
		entity: 'dealsFilters' | 'contactsFilters' | 'companiesFilters' | 'leadsFilters',
	) {
		const key = entity.replace('Filters', '') as any;
		const title = getTitleFromEntityType(key);
		return [
			{ title, dataIndex: 'key', width: 230 },
			{ title: '', dataIndex: 'value' },
		];
	}

	function getTitleFromEntityType(entity: 'deals' | 'contacts' | 'companies' | 'leads') {
		if (entity === 'deals') return dictionary.deals;
		if (entity === 'contacts') return dictionary.contacts;
		if (entity === 'companies') return dictionary.companies;
		if (entity === 'leads') return dictionary.leads;
	}

	function handleAudienceTypeChange(value: number) {
		setChosenSegments([]);
		setAudienceType(value);
		props.onChangeAudience('');
		props.onChangeSegment([]);
	}

	return (
		<>
			<SegmentModal
				visible={segmentModalIsVisible}
				onSubmit={fetchSegments}
				onCancel={() => setSegmentModalIsVisible(false)}
			/>
			<Row style={{ paddingTop: '2em' }}>
				<Title level={2}>
					<Text type="gray-9">{`${dictionary.define_campaign_audience}:`}</Text>
				</Title>
			</Row>
			<Row style={{ textAlign: 'left' }}>
				<Title level={4}>
					<Text type="gray-9">{`${dictionary.source_number}:`}</Text>
				</Title>
				<Form.Item validateStatus={originStatus} help={props.originNumberMessage}>
					<Select
						{...{
							defaultValue: props.originNumber || undefined,
							size: 'large',
							style: { width: '100%' },
							placeholder: dictionary.select_source_number,
							onChange: props.onChangeOriginNumber,
							loading: activeNumbers.length === 0,
						}}
					>
						{activeNumbers.map((number) => (
							<Select.Option key={number.number} value={number.number}>
								{number.nickname}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
				<Row style={{ marginTop: '24px' }} />
				<Title level={4}>
					<Text type="gray-9">{`${dictionary.audience}:`}</Text>
				</Title>
				<Row>
					<Col span={24}>
						<Select
							{...{
								placeholder: dictionary.select_audience_type,
								size: 'large',
								style: { width: '100%' },
								value: audienceType,
								onChange: handleAudienceTypeChange,
							}}
						>
							<Select.Option value={0}>{dictionary.static_list}</Select.Option>
							<Select.Option value={1}>{dictionary.dynamic_list}</Select.Option>
						</Select>
					</Col>
				</Row>
			</Row>
			{audienceType === 0 && (
				<Row style={{ margin: '8px 0' }}>
					<Form.Item
						validateStatus={audienceStatus}
						help={props.audienceMessage}
						style={{ width: '100%', textAlign: 'left', marginBottom: -1 }}
					>
						<TextArea
							rows={4}
							style={{ width: '100%' }}
							name={props.audienceFieldName}
							placeholder={dictionary.custom_recipients}
							autoFocus
							value={props.audience}
							onChange={(e) => props.onChangeAudience(e.target.value)}
						/>
					</Form.Item>
				</Row>
			)}
			{audienceType === 1 && (
				<>
					<Row style={{ margin: '8px 0' }}>
						<MultiSelect
							searchable
							placeholder={chosenSegments.length === 0 ? dictionary.select_segments : ''}
							data={selectSegmentsItems}
							value={chosenSegments}
							onChange={handleSelectSegments}
							styles={{
								root: { width: '100%' },
								input: {
									minHeight: 40,
									overflow: 'auto',
									fontSize: 16,
									borderColor: segmentStatus,
								},
								pill: {
									color: 'white',
									backgroundColor: 'var(--mantine-primary-color-filled)',
								},
							}}
							comboboxProps={{ withinPortal: false }}
							rightSectionPointerEvents="visible"
							rightSection={
								<Button
									onClick={toggleSegmentModal}
									type="primary"
									size="large"
									style={{
										height: '100%',
										marginLeft: -12,
										borderTopLeftRadius: 0,
										borderBottomLeftRadius: 0,
									}}
								>
									<PlusOutlined />
								</Button>
							}
						/>
						{props.segmentMessage && (
							<Row style={{ margin: '2px 0', textAlign: 'start' }}>
								<Text type="danger">{props.segmentMessage}</Text>
							</Row>
						)}
					</Row>
				</>
			)}

			<Row style={{ textAlign: 'left', marginTop: '24px' }}>
				{chosenSegmentsHasFilters() && (
					<Title level={4}>
						<Text type="gray-9">{`${dictionary.segment_filters}:`}</Text>
					</Title>
				)}
			</Row>
			{audienceType === 1 && (
				<Row>
					{getFormattedChosenSegments().map(([key, value]: any) => {
						if (value.length === 0) return null;
						return (
							<Row key={key} style={{ margin: '8px 0' }}>
								<Card style={{ borderRadius: 6, border: 0 }} bodyStyle={{ padding: 0 }}>
									<Table
										size="small"
										pagination={false}
										columns={getColums(key)}
										dataSource={value.map(([k, v]: any) => ({
											key: k,
											value: v,
										}))}
									/>
								</Card>
							</Row>
						);
					})}
				</Row>
			)}

			<Row style={{ height: 50 }}></Row>
		</>
	);
}

export default CampaignAudience;
